// src/theme.js
// iOS-Style Design System for Solar Gard CRM

import { createTheme } from '@mui/material/styles';

// iOS Renk Paleti
const iosColors = {
  blue: '#007AFF',
  green: '#34C759',
  indigo: '#5856D6',
  orange: '#FF9500',
  pink: '#FF2D55',
  purple: '#AF52DE',
  red: '#FF3B30',
  teal: '#5AC8FA',
  yellow: '#FFCC00',
  gray: {
    1: '#8E8E93',
    2: '#AEAEB2',
    3: '#C7C7CC',
    4: '#D1D1D6',
    5: '#E5E5EA',
    6: '#F2F2F7',
  },
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: iosColors.blue,
      light: '#66B2FF',
      dark: '#0055CC',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: iosColors.orange,
      light: '#FFB84D',
      dark: '#CC7A00',
    },
    success: {
      main: iosColors.green,
      light: '#6EE793',
      dark: '#2A9F47',
    },
    error: {
      main: iosColors.red,
      light: '#FF6B66',
      dark: '#CC2F26',
    },
    warning: {
      main: iosColors.yellow,
      light: '#FFE066',
      dark: '#CCA300',
    },
    info: {
      main: iosColors.teal,
      light: '#8ED9FC',
      dark: '#48A0C8',
    },
    background: {
      default: iosColors.gray[6],
      paper: 'rgba(255, 255, 255, 0.85)',
    },
    text: {
      primary: '#1C1C1E',
      secondary: '#3C3C43',
      disabled: iosColors.gray[1],
    },
    divider: 'rgba(60, 60, 67, 0.12)',
    action: {
      hover: 'rgba(0, 122, 255, 0.08)',
      selected: 'rgba(0, 122, 255, 0.12)',
      focus: 'rgba(0, 122, 255, 0.16)',
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#3C3C43',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#3C3C43',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      color: iosColors.gray[1],
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9375rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.04)',
    '0 2px 4px rgba(0, 0, 0, 0.06)',
    '0 4px 8px rgba(0, 0, 0, 0.06)',
    '0 6px 12px rgba(0, 0, 0, 0.08)',
    '0 8px 16px rgba(0, 0, 0, 0.08)',
    '0 12px 24px rgba(0, 0, 0, 0.1)',
    '0 16px 32px rgba(0, 0, 0, 0.1)',
    '0 20px 40px rgba(0, 0, 0, 0.12)',
    '0 24px 48px rgba(0, 0, 0, 0.12)',
    '0 28px 56px rgba(0, 0, 0, 0.14)',
    '0 32px 64px rgba(0, 0, 0, 0.14)',
    ...Array(13).fill('0 32px 64px rgba(0, 0, 0, 0.14)'),
  ],
  components: {
    // Faz 4.5 (2026-08-29): tüm metin girdileri YAZARKEN de büyük görünür.
    // Kayıt değeri ayrıca yazma yollarında toLocaleUpperCase('tr-TR') ile garanti edilir.
    // E-posta / şifre / sayı alanları hariçtir (text-transform değer değiştirmez, yalnız görünüm).
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&:not([type="email"]):not([type="password"]):not([type="number"])': {
            textTransform: 'uppercase',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: iosColors.gray[3],
            borderRadius: '4px',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 16,
          backdropFilter: 'blur(20px)',
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
          overflow: 'hidden',
          transition: 'transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1), box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 20px',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.25, 0.1, 0.25, 1)',
          '&:hover': {
            boxShadow: 'none',
            transform: 'scale(1.02)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${iosColors.blue} 0%, #0055CC 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, #0066DD 0%, #004499 100%)`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${iosColors.orange} 0%, #CC7A00 100%)`,
        },
        containedSuccess: {
          background: `linear-gradient(135deg, ${iosColors.green} 0%, #2A9F47 100%)`,
        },
        containedError: {
          background: `linear-gradient(135deg, ${iosColors.red} 0%, #CC2F26 100%)`,
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
            backgroundColor: 'rgba(0, 122, 255, 0.08)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(0, 122, 255, 0.08)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 122, 255, 0.08)',
            transform: 'scale(1.05)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 16px rgba(0, 122, 255, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0, 122, 255, 0.4)',
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backdropFilter: 'blur(20px)',
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          borderBottom: '1px solid rgba(60, 60, 67, 0.12)',
          color: '#1C1C1E',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRight: '1px solid rgba(60, 60, 67, 0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: iosColors.blue,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          transition: 'all 0.2s ease',
        },
        filled: {
          '&:hover': {
            transform: 'scale(1.02)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 52,
          height: 32,
          padding: 0,
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            '& + .MuiSwitch-track': {
              backgroundColor: iosColors.green,
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 28,
          height: 28,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        },
        track: {
          borderRadius: 16,
          backgroundColor: iosColors.gray[3],
          opacity: 1,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '1.25rem',
          padding: '20px 24px 16px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 20px',
          gap: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(60, 60, 67, 0.12)',
          padding: '16px',
        },
        head: {
          color: '#3C3C43',
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: 'rgba(242, 242, 247, 0.8)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 122, 255, 0.04)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '2px 8px',
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 122, 255, 0.12)',
            color: iosColors.blue,
            '&:hover': {
              backgroundColor: 'rgba(0, 122, 255, 0.16)',
            },
            '& .MuiListItemIcon-root': {
              color: iosColors.blue,
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 122, 255, 0.08)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          color: '#3C3C43',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9375rem',
          minHeight: 48,
          transition: 'color 0.2s ease',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        standardSuccess: {
          backgroundColor: 'rgba(52, 199, 89, 0.12)',
          color: '#2A9F47',
        },
        standardError: {
          backgroundColor: 'rgba(255, 59, 48, 0.12)',
          color: '#CC2F26',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 204, 0, 0.12)',
          color: '#997A00',
        },
        standardInfo: {
          backgroundColor: 'rgba(0, 122, 255, 0.12)',
          color: '#0055CC',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(28, 28, 30, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 8,
          fontSize: '0.8125rem',
          padding: '8px 12px',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${iosColors.blue} 0%, ${iosColors.purple} 100%)`,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: iosColors.blue,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(60, 60, 67, 0.12)',
        },
      },
    },
  },
});

export default theme;
